/**
 * Analytics Service for Bistró Pont10
 * Captures anonymous user behavior for commercial intelligence
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session management
const SESSION_KEY = 'pont10_session_id';
const DEVICE_KEY = 'pont10_device_id';

// Generate UUID
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Get or create session ID (resets on page close)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = generateUUID();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
};

// Get or create device ID (persists across sessions)
const getDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_KEY);
  if (!deviceId) {
    deviceId = generateUUID();
    localStorage.setItem(DEVICE_KEY, deviceId);
  }
  return deviceId;
};

// Event queue for batch sending
let eventQueue = [];
let flushTimeout = null;

// Current context
let currentContext = {
  lang: 'es',
  screen: '/'
};

/**
 * Update current context
 */
export const setAnalyticsContext = (context) => {
  currentContext = { ...currentContext, ...context };
};

/**
 * Track an analytics event
 */
export const trackEvent = async (eventName, properties = {}) => {
  try {
    const event = {
      session_id: getSessionId(),
      device_id: getDeviceId(),
      lang: currentContext.lang,
      timestamp: new Date().toISOString(),
      screen: currentContext.screen,
      event_name: eventName,
      properties
    };

    // Add to queue
    eventQueue.push(event);

    // Flush after short delay (batch events)
    if (flushTimeout) clearTimeout(flushTimeout);
    flushTimeout = setTimeout(flushEvents, 500);

    // Immediate flush for important events
    if (['show_to_waiter_clicked', 'whatsapp_share_clicked', 'session_end'].includes(eventName)) {
      await flushEvents();
    }
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

/**
 * Send queued events to server
 */
const flushEvents = async () => {
  if (eventQueue.length === 0) return;

  const events = [...eventQueue];
  eventQueue = [];

  try {
    await fetch(`${API_URL}/api/analytics/events/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events })
    });
  } catch (error) {
    // Re-queue events on failure
    eventQueue = [...events, ...eventQueue];
    console.error('Failed to send analytics:', error);
  }
};

// ============================================
// SPECIFIC EVENT TRACKING FUNCTIONS
// ============================================

// Navigation Events
export const trackSessionStart = () => {
  trackEvent('session_start', {
    referrer: document.referrer,
    userAgent: navigator.userAgent,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight
  });
};

export const trackSessionEnd = (durationMs) => {
  trackEvent('session_end', {
    duration_ms: durationMs,
    duration_seconds: Math.round(durationMs / 1000)
  });
};

export const trackLanguageSelected = (lang) => {
  setAnalyticsContext({ lang });
  trackEvent('language_selected', { lang });
};

export const trackSectionOpened = (section) => {
  setAnalyticsContext({ screen: `/${section}` });
  trackEvent('section_opened', { section });
};

export const trackCategoryOpened = (categoryId, categoryName) => {
  setAnalyticsContext({ screen: `/category/${categoryId}` });
  trackEvent('category_opened', { 
    category_id: categoryId,
    category_name: categoryName
  });
};

// Product Events
export const trackProductImpression = (productId, productName, categoryId, position) => {
  trackEvent('product_impression', {
    product_id: productId,
    product_name: productName,
    category_id: categoryId,
    position
  });
};

export const trackProductOpened = (product) => {
  if (!product) return;
  trackEvent('product_opened', {
    product_id: product.id,
    product_name: product.name?.es || product.name || '',
    category_id: product.category_id,
    price: product.price
  });
};

export const trackProductClosed = (productId, timeSpentMs) => {
  trackEvent('product_closed', {
    product_id: productId,
    time_spent_ms: timeSpentMs
  });
};

export const trackAddToSelection = (product, quantity = 1, selectedOptions = []) => {
  if (!product) return;
  trackEvent('add_to_selection', {
    product_id: product.id,
    product_name: product.name?.es || product.name || '',
    category_id: product.category_id,
    price: product.price,
    quantity,
    has_options: selectedOptions.length > 0,
    options_count: selectedOptions.length
  });
};

export const trackRemoveFromSelection = (productId, productName) => {
  trackEvent('remove_from_selection', {
    product_id: productId,
    product_name: productName
  });
};

export const trackQtyChanged = (productId, oldQty, newQty) => {
  trackEvent('qty_changed', {
    product_id: productId,
    old_qty: oldQty,
    new_qty: newQty,
    change: newQty - oldQty
  });
};

// Suggestion Events
export const trackSuggestionTypeClicked = (type) => {
  trackEvent('suggestion_type_clicked', { type });
};

export const trackSuggestionViewed = (productId, productName, suggestionType) => {
  trackEvent('suggestion_viewed', {
    product_id: productId,
    product_name: productName,
    suggestion_type: suggestionType
  });
};

export const trackSuggestionAdd = (product, suggestionType) => {
  if (!product) return;
  trackEvent('suggestion_add', {
    product_id: product.id,
    product_name: product.name?.es || product.name || '',
    category_id: product.category_id,
    price: product.price,
    suggestion_type: suggestionType
  });
};

// Conversion Events
export const trackSelectionOpened = (itemCount, totalValue) => {
  trackEvent('selection_opened', {
    item_count: itemCount,
    total_value: totalValue
  });
};

export const trackShowToWaiterClicked = (itemCount, totalValue) => {
  trackEvent('show_to_waiter_clicked', {
    item_count: itemCount,
    total_value: totalValue
  });
};

export const trackWhatsAppShareClicked = (itemCount, totalValue) => {
  trackEvent('whatsapp_share_clicked', {
    item_count: itemCount,
    total_value: totalValue
  });
};

// Allergen Events
export const trackAllergenViewed = (productId, allergens) => {
  trackEvent('allergen_viewed', {
    product_id: productId,
    allergens
  });
};

// ============================================
// SESSION LIFECYCLE
// ============================================

let sessionStartTime = null;

export const initAnalytics = () => {
  sessionStartTime = Date.now();
  trackSessionStart();

  // Track session end on page unload
  window.addEventListener('beforeunload', () => {
    if (sessionStartTime) {
      const duration = Date.now() - sessionStartTime;
      trackSessionEnd(duration);
      // Force immediate send
      navigator.sendBeacon(
        `${API_URL}/api/analytics/events/batch`,
        new Blob([JSON.stringify({ events: eventQueue })], { type: 'application/json' })
      );
    }
  });

  // Also track on visibility change (mobile tab switch)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && sessionStartTime) {
      flushEvents();
    }
  });
};

export default {
  init: initAnalytics,
  setContext: setAnalyticsContext,
  track: trackEvent,
  trackLanguageSelected,
  trackSectionOpened,
  trackCategoryOpened,
  trackProductImpression,
  trackProductOpened,
  trackProductClosed,
  trackAddToSelection,
  trackRemoveFromSelection,
  trackQtyChanged,
  trackSuggestionTypeClicked,
  trackSuggestionViewed,
  trackSuggestionAdd,
  trackSelectionOpened,
  trackShowToWaiterClicked,
  trackWhatsAppShareClicked,
  trackAllergenViewed
};
